import React, { useEffect, useState } from "react";
import { Box, Typography, Button, IconButton, Select, MenuItem, Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Drawer from "@mui/material/Drawer";
import InputField from "../../../../Components/Masters/InputField";
import SelectField from "../../../../Components/Masters/SelectField";
import EditDialog from "../../../../Components/Common/EditDialog";
import { post, put } from "../../../../utils/api";
import countries from "../../Roles and Permissions/Country.json";

const PROCESSING_METHOD_OPTIONS = [
  "Recycling",
  "Shredding",
  "Incineration",
  "Composting",
  "Melting",
  "Crushing",
  "Chemical Treatment",
  "Refurbishment",
  "Packaging",
  "Sorting",
];

function AddCustomerMaster({
  formInputData,
  fetchData,
  id,
  element,
  setAddData,
  addData,
  setsnackOpen,
  addUrl,
  updateUrl,
}) {
  const [errors, setErrors] = useState({});
  const [editDialog, setEditDialog] = useState(false);

  const buildInitialState = () => ({
    customerVendorName: element?.customerVendorName || "",
    productName:
      element?.productName?._id || element?.productName?.id || element?.productName || "",
    picName: element?.picName || "",
    country: element?.country || "",
    email: element?.email || "",
    processingMethodsUsed: Array.isArray(element?.processingMethodsUsed)
      ? element.processingMethodsUsed
      : element?.processingMethodsUsed
      ? [element.processingMethodsUsed]
      : [],
  });

  const [formData, setFormData] = useState(buildInitialState);
  const [formData2, setFormData2] = useState(buildInitialState);

  useEffect(() => {
    const next = buildInitialState();
    setFormData(next);
    setFormData2(next);
  }, [element, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleExitAgree = () => {
    setEditDialog(false);
    setAddData(false);
    setErrors({});
    const cleared = buildInitialState();
    setFormData(cleared);
    setFormData2(cleared);
  };

  const handleClose = () => {
    const unchanged = Object.keys(formData).every((key) => formData[key] === formData2[key]);
    if (unchanged) handleExitAgree();
    else setEditDialog(true);
  };

  const validateForm = () => {
    const next = {};
    formInputData.forEach((input) => {
      const value = formData[input.fieldName];
      if (
        (Array.isArray(value) && value.length === 0) ||
        (!Array.isArray(value) && (!value || String(value).trim() === ""))
      ) {
        next[input.fieldName] = `${input.title} is required`;
      }
    });

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      next.email = "Enter a valid email";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const Save = async () => {
    if (!validateForm()) return;

    const payload = {
      customerVendorName: formData.customerVendorName.trim(),
      productName: formData.productName,
      picName: formData.picName.trim(),
      country: formData.country,
      email: formData.email.trim(),
      processingMethodsUsed: formData.processingMethodsUsed,
    };

    const request = id ? put(`${updateUrl}?Id=${id}`, payload) : post(addUrl, payload);

    await request
      .then((res) => {
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        fetchData();
        handleExitAgree();
      })
      .catch((err) => {
        const apiMessage = err?.response?.data?.message || "Something went wrong";
        setsnackOpen({
          open: true,
          message: apiMessage,
          severity: "error",
        });
      });
  };

  const renderField = (el) => {
    if (el.fieldName === "productName") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.productName}
          handleChange={handleChange}
          url="/master/product/getAll"
          field="productName"
          errors={errors[el.fieldName]}
        />
      );
    }
    if (el.fieldName === "country") {
      return (
        <SelectField
          key={el.fieldName}
          title={el.title}
          name={el.fieldName}
          value={formData.country}
          handleChange={handleChange}
          options={countries.countries}
          field="name"
          errors={errors[el.fieldName]}
        />
      );
    }
    if (el.fieldName === "processingMethodsUsed") {
      return (
        <Box key={el.fieldName}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mt: 1 }}>
            {el.title}
          </Typography>
          <Select
            multiple
            fullWidth
            size="small"
            style={{ marginBottom: "10px", marginTop: "8px" }}
            name={el.fieldName}
            value={formData.processingMethodsUsed || []}
            onChange={handleChange}
            displayEmpty
            renderValue={(selected) =>
              !selected || selected.length === 0 ? "Tap to Select" : selected.join(", ")
            }
          >
            <MenuItem value="" disabled>
              Tap to Select
            </MenuItem>
            {PROCESSING_METHOD_OPTIONS.map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </Select>

          {(formData.processingMethodsUsed || []).map((value) => (
            <Chip
              key={value}
              label={value}
              sx={{
                bgcolor: "#1C486B",
                color: "white",
                height: "28px",
                m: 0.25,
                "& .MuiChip-deleteIcon": {
                  color: "white",
                  "&:hover": {
                    color: "white",
                  },
                },
              }}
              onDelete={() =>
                handleChange({
                  target: {
                    name: "processingMethodsUsed",
                    value: (formData.processingMethodsUsed || []).filter((v) => v !== value),
                  },
                })
              }
            />
          ))}

          {errors[el.fieldName] && (
            <Typography variant="body2" color="#D34040" sx={{ mt: 0.5 }}>
              {errors[el.fieldName]}
            </Typography>
          )}
        </Box>
      );
    }

    return (
      <InputField
        key={el.fieldName}
        title={el.title}
        handleChange={handleChange}
        placeholder={el.placeholder}
        value={formData[el.fieldName] || ""}
        errors={errors[el.fieldName]}
        fieldName={el.fieldName}
      />
    );
  };

  const drawerList = () => (
    <Box display="flex" gap={3} sx={{ width: "28rem", maxWidth: "100%" }}>
      {editDialog && <EditDialog {...{ editDialog, setEditDialog, handleExitAgree }} />}
      <Box
        sx={{
          backgroundColor: "#fff",
          height: "20px",
          padding: "13px",
          margin: "30px 0 0 0",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          borderRadius: "80%",
          width: "5%",
        }}
      >
        <IconButton onClick={handleClose} sx={{ color: "black" }}>
          <CloseIcon sx={{ fontSize: "1.2rem" }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          width: "90%",
          p: 3,
          backgroundColor: "#fff",
          height: "100vh",
          overflowY: "scroll",
        }}
      >
        <Typography variant="h6" sx={{ textDecoration: "underline", fontWeight: "bold", mb: 2 }}>
          {id ? "Edit Customer" : "Add Customer"}
        </Typography>
        {formInputData.map((el) => renderField(el))}
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: "#0a3f73",
              textTransform: "none",
              marginTop: "10%",
            }}
            onClick={Save}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      sx={{
        backgroundColor: "transparent",
        height: "100vh",
        minHeight: "100vh",
        overflowY: "auto",
        "& .MuiDrawer-paper": {
          backgroundColor: "transparent",
          boxShadow: "none",
          height: "100vh",
          minHeight: "100vh",
          overflowY: "auto",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
      anchor="right"
      open={addData}
      onClose={handleClose}
    >
      {drawerList()}
    </Drawer>
  );
}

export default AddCustomerMaster;
